import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet';

import Widget from './Widget';
import Modal from 'components/Common/MUIModal/Modal';
import ModalBody from 'components/Common/MUIModal/ModalBody';
import ModalActions from 'components/Common/MUIModal/ModalActions';
import Button from 'components/UI/Buttons/Button/Button';
import { useCustomer } from 'hooks/useCustomer';
import useJQuery from 'hooks/useJQuery';
import { MondialRelayAddress, MondialRelayModalProps } from './types';

import styles from './MondialRelay.module.scss';

type Relay = MondialRelayAddress | undefined;

export default function MondialRelayModal({
  isOpen,
  onClose,
  widgetReady,
  onSelect,
  currentSelected,
}: MondialRelayModalProps): JSX.Element | null {
  const { t } = useTranslation();
  const { primaryAddress } = useCustomer();
  const { isLoaded: isJqueryImported } = useJQuery();
  const [isLeafletImported, setLeafletImported] = useState(false);
  const [selectAddress, setSelectAddress] = useState<Relay>(currentSelected);

  useEffect(() => {
    if (!document.getElementById('leaflet-script')) {
      const leafletScript = document.createElement('script');
      leafletScript.src = '//unpkg.com/leaflet/dist/leaflet.js';
      leafletScript.id = 'leaflet-script';
      document.body.appendChild(leafletScript);
      leafletScript.addEventListener('load', () => {
        setLeafletImported(true);
      });
    } else {
      setLeafletImported(true);
    }
  }, []);

  useEffect(() => {
    if (isJqueryImported && isLeafletImported) {
      if (!document.getElementById('mondialrelay-js')) {
        const mondialRelayScript = document.createElement('script');
        mondialRelayScript.id = 'mondialrelay-js';
        mondialRelayScript.src =
          'https://widget.mondialrelay.com/parcelshop-picker/jquery.plugin.mondialrelay.parcelshoppicker.min.js';

        document.body.appendChild(mondialRelayScript);
        mondialRelayScript.addEventListener('load', () => {
          widgetReady();
        });
      } else {
        widgetReady();
      }
    }
  }, [isJqueryImported, isLeafletImported]);

  if (!isOpen) {
    return null;
  }

  return (
    <>
      <Helmet>
        <link
          rel="stylesheet"
          type="text/css"
          href="//unpkg.com/leaflet/dist/leaflet.css"
        />
      </Helmet>
      <Modal
        open={isOpen}
        className={styles.modal}
        classes={{ innerContainer: styles.modalWrapper }}
      >
        <ModalBody
          className={styles.modalContainer}
          bottomDivider={false}
          topDivider={false}
        >
          <Widget
            postalCode={primaryAddress?.postalCode}
            city={primaryAddress?.city}
            currentSelected={currentSelected}
            onSelect={setSelectAddress}
          />
        </ModalBody>
        <ModalActions className={styles.modalActions}>
          <Button onClick={onClose} model={'secondary'}>
            {t('LABEL_CLOSE')}
          </Button>
          <Button
            model={'primary'}
            disabled={!selectAddress}
            onClick={() => selectAddress && onSelect(selectAddress)}
          >
            {t('SELECT')}
          </Button>
        </ModalActions>
      </Modal>
    </>
  );
}
